import { BuildingLogItem } from '../components/buildingLog/BuildingLog';

export interface GroupedLogData {
  changeGroups: { date: string; logs: BuildingLogItem[] }[];
  noChangeGroups: { startDate: string; endDate: string; logs: BuildingLogItem[] }[];
}

// Číslo jednotky pro řazení (z "1510/101" bereme 101; bez jednotky řadíme nakonec)
const parseUnitNumber = (label?: string): number => {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const part = label.includes('/') ? label.split('/').pop()! : label;
  const parsed = parseInt(part, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const getLogUnitNumber = (log: BuildingLogItem): number => {
  if (log.units && log.units.length > 0) {
    return parseUnitNumber(log.units[0].label);
  }
  if (Array.isArray(log.data) && log.data.length > 0) {
    return parseUnitNumber((log.data[0] as { unit?: string }).unit);
  }
  return Number.MAX_SAFE_INTEGER;
};

// Pořadí typů v rámci jednotky: odebrání (0) → změna/převod (1) → přidání (2)
const getLogTypeRank = (log: BuildingLogItem): number => {
  if (Array.isArray(log.data)) {
    const types = log.data.map(item => (item as { type?: string }).type);
    const hasRemove = types.includes('removeProfile');
    const hasAdd = types.includes('addProfile') || types.includes('addUnit');
    if (hasRemove && !hasAdd) return 0;
    if (hasAdd && !hasRemove) return 2;
    return 1;
  }
  return 1; // value-diff "Vlastník" = změna
};

// Řazení změn pro zobrazení: vzestupně dle čísla jednotky, u jedné jednotky odebrání → přidání.
// Sdílené mezi "Historie kontrol" (denní skupiny) i "Poslední kontrola".
export const compareBuildingLogsForDisplay = (a: BuildingLogItem, b: BuildingLogItem): number => {
  const unitDiff = getLogUnitNumber(a) - getLogUnitNumber(b);
  return unitDiff !== 0 ? unitDiff : getLogTypeRank(a) - getLogTypeRank(b);
};

export const groupLogsByDate = (logs: BuildingLogItem[]): { date: string; logs: BuildingLogItem[] }[] => {
  const groups: { date: string; logs: BuildingLogItem[] }[] = [];

  logs.forEach(log => {
    const dateStr = new Date(log.recordedAt).toISOString().split('T')[0];
    let group = groups.find(g => g.date === dateStr);
    if (!group) {
      group = { date: dateStr, logs: [] };
      groups.push(group);
    }
    group.logs.push(log);
  });

  // V rámci dne: vzestupně dle čísla jednotky, u jedné jednotky odebrání → přidání
  groups.forEach(group => {
    group.logs.sort(compareBuildingLogsForDisplay);
  });

  return groups;
};

export const groupBuildingLogsByChangeStatus = (logs: BuildingLogItem[]): GroupedLogData => {
  const sortedLogs = [...logs].sort((a, b) => 
    new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
  );

  const logsWithChanges = sortedLogs.filter(log => log.isChanged);
  const logsWithoutChanges = sortedLogs.filter(log => !log.isChanged);

  const changeGroups = groupLogsByDate(logsWithChanges);
  const changeDates = changeGroups
    .map(group => new Date(group.date))
    .sort((a, b) => b.getTime() - a.getTime());

  const noChangeGroups: { startDate: string; endDate: string; logs: BuildingLogItem[] }[] = [];

  // Vytvořit kontinuální intervaly mezi změnami
  const intervals = createContinuousDateIntervals(changeDates, logsWithoutChanges);
  
  intervals.forEach(interval => {
    const logsInInterval = filterLogsByInterval(logsWithoutChanges, interval);
    
    // Vytvořit nochange group i když nejsou žádné logy v intervalu
    // Pro kontinuální timeline
    if (interval.hasValidRange) {
      noChangeGroups.push({
        startDate: interval.startDate!,
        endDate: interval.endDate!,
        logs: logsInInterval.sort((a, b) => 
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
        )
      });
    }
  });

  return { changeGroups, noChangeGroups };
};

const createContinuousDateIntervals = (changeDates: Date[], logsWithoutChanges: BuildingLogItem[]): Array<{ 
  start: Date | null; 
  end: Date | null; 
  startDate?: string; 
  endDate?: string; 
  hasValidRange: boolean 
}> => {
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Konec dnešního dne

  // Žádné změny (např. nové SVJ, zatím jen kontroly "beze změny") → jediný interval
  // od dneška po nejstarší log, jinak by timeline byla prázdná.
  if (changeDates.length === 0) {
    if (logsWithoutChanges.length === 0) {
      return [];
    }

    const oldestLogDate = new Date(Math.min(...logsWithoutChanges.map(log => new Date(log.recordedAt).getTime())));

    return [{
      start: null,
      end: null,
      startDate: today.toISOString(),
      endDate: oldestLogDate.toISOString(),
      hasValidRange: true
    }];
  }

  const intervals: Array<{
    start: Date | null;
    end: Date | null;
    startDate?: string;
    endDate?: string;
    hasValidRange: boolean
  }> = [];

  // Interval od dneška do první (nejnovější) změny
  if (changeDates.length > 0) {
    const firstChangeDate = changeDates[0];
    const dayAfterFirstChange = new Date(firstChangeDate);
    dayAfterFirstChange.setDate(dayAfterFirstChange.getDate() + 1);
    
    // Pouze pokud je mezi dnes a změnou alespoň den rozdíl
    if (today.getTime() > dayAfterFirstChange.getTime()) {
      intervals.push({
        start: null,
        end: null,
        startDate: today.toISOString(),
        endDate: dayAfterFirstChange.toISOString(),
        hasValidRange: true
      });
    }
  }

  // Intervaly mezi změnami
  for (let i = 0; i < changeDates.length - 1; i++) {
    const currentChangeDate = changeDates[i];
    const nextChangeDate = changeDates[i + 1];
    
    const dayBeforeCurrentChange = new Date(currentChangeDate);
    dayBeforeCurrentChange.setDate(dayBeforeCurrentChange.getDate() - 1);
    dayBeforeCurrentChange.setHours(23, 59, 59, 999);
    
    const dayAfterNextChange = new Date(nextChangeDate);
    dayAfterNextChange.setDate(dayAfterNextChange.getDate() + 1);
    
    // Pouze pokud je mezi změnami alespoň den rozdíl
    if (dayBeforeCurrentChange.getTime() > dayAfterNextChange.getTime()) {
      intervals.push({
        start: currentChangeDate,
        end: nextChangeDate,
        startDate: dayBeforeCurrentChange.toISOString(),
        endDate: dayAfterNextChange.toISOString(),
        hasValidRange: true
      });
    }
  }

  // Interval od nejstarší změny do začátku všech dat
  if (changeDates.length > 0 && logsWithoutChanges.length > 0) {
    const oldestChangeDate = changeDates[changeDates.length - 1];
    const dayBeforeOldestChange = new Date(oldestChangeDate);
    dayBeforeOldestChange.setDate(dayBeforeOldestChange.getDate() - 1);
    dayBeforeOldestChange.setHours(23, 59, 59, 999);
    
    // Najít nejstarší datum ze všech no-change logů
    const oldestLogDate = new Date(Math.min(...logsWithoutChanges.map(log => new Date(log.recordedAt).getTime())));
    
    // Pouze pokud existuje období před nejstarší změnou
    if (dayBeforeOldestChange.getTime() > oldestLogDate.getTime()) {
      intervals.push({
        start: oldestChangeDate,
        end: null,
        startDate: dayBeforeOldestChange.toISOString(),
        endDate: oldestLogDate.toISOString(),
        hasValidRange: true
      });
    }
  }

  return intervals;
};

const filterLogsByInterval = (
  logs: BuildingLogItem[], 
  interval: { start: Date | null; end: Date | null; startDate?: string; endDate?: string }
): BuildingLogItem[] => {
  return logs.filter(log => {
    const logDate = new Date(log.recordedAt);
    
    // Pokud máme explicitní startDate a endDate, použijeme je
    if (interval.startDate && interval.endDate) {
      const intervalStart = new Date(interval.startDate);
      const intervalEnd = new Date(interval.endDate);
      return logDate <= intervalStart && logDate >= intervalEnd;
    }
    
    // Jinak použijeme původní logiku
    const afterStart = interval.start ? logDate < interval.start : true;
    const beforeEnd = interval.end ? logDate > interval.end : true;
    const notOnStartDate = interval.start ? 
      logDate.toISOString().split('T')[0] !== interval.start.toISOString().split('T')[0] : true;
    const notOnEndDate = interval.end ? 
      logDate.toISOString().split('T')[0] !== interval.end.toISOString().split('T')[0] : true;
    return afterStart && beforeEnd && notOnStartDate && notOnEndDate;
  });
};

export const createTimeline = (groupedData: GroupedLogData): Array<{
  type: 'change' | 'noChange';
  key: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  logs: BuildingLogItem[];
}> => {
  const timeline: Array<{
    type: 'change' | 'noChange';
    key: string;
    date?: string;
    startDate?: string;
    endDate?: string;
    logs: BuildingLogItem[];
  }> = [
    ...groupedData.changeGroups.map(group => ({
      type: 'change' as const,
      key: `change-${group.date}`,
      date: group.date,
      logs: group.logs
    })),
    ...groupedData.noChangeGroups.map(group => ({
      type: 'noChange' as const,
      key: `noChange-${group.startDate}-${group.endDate}`,
      startDate: group.startDate,
      endDate: group.endDate,
      logs: group.logs
    }))
  ];

  timeline.sort((a, b) => {
    const dateA = a.type === 'change' ? new Date(a.date!) : new Date(a.startDate!);
    const dateB = b.type === 'change' ? new Date(b.date!) : new Date(b.startDate!);
    return dateB.getTime() - dateA.getTime();
  });

  return timeline;
};
